import { version } from '../../../package.json';

export default class CIndexedDB<T> {
  private static readonly databaseName: string = 'TekDashboard';
  protected readonly storeName: string;
  protected database: IDBDatabase;

  constructor(pInstancia: IDBDatabase, pStoreName: string) {
    this.storeName = pStoreName;
    this.database = pInstancia;
  }

  protected loggarErro(chave: string, erro: string) {
    console.group(this.constructor.name);
    console.error(`[${this.storeName}] - [${chave}]: ${erro}`);
    console.groupEnd();
  }

  static async inicializar(pStoreNames: string[]): Promise<IDBDatabase> {
    // Abre o banco na versão atual (seja ela qual for) apenas para "espiar"
    const lDatabaseAtual = await new Promise<IDBDatabase>((resolve, reject) => {
      // Ao não passar a versão, o IndexedDB abre na versão mais recente disponível
      const request = indexedDB.open(this.databaseName);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    const lVersaoAtual = lDatabaseAtual.version;
    const lStoresExistentes = lDatabaseAtual.objectStoreNames;

    const lPrecisaAtualizar = pStoreNames.some((name) => !lStoresExistentes.contains(name));

    if (lPrecisaAtualizar) {
      //  Se precisamos criar stores novos, fechamos a conexão de "espiada"...
      lDatabaseAtual.close();
    } else {
      // Se não precisa atualizar, apenas configura a proteção contra deadlocks e devolve o banco pronto
      lDatabaseAtual.onversionchange = () => {
        lDatabaseAtual.close();
        console.warn(`[IndexedDB] Conexão fechada para permitir atualização em outra aba.`);
      };
      return lDatabaseAtual;
    }

    // Reabre somando +1 na versão para forçar o evento 'onupgradeneeded'
    return new Promise((resolve, reject) => {
      const lNovaVersao = lVersaoAtual + 1;
      const lRequestUpgrade = indexedDB.open(this.databaseName, lNovaVersao);

      lRequestUpgrade.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const db = (event.target as IDBOpenDBRequest).result;

        for (const name of pStoreNames) {
          if (!db.objectStoreNames.contains(name)) {
            db.createObjectStore(name, { keyPath: 'key' });
            console.log(`[IndexedDB] Store ${name} criado na versão ${lNovaVersao}.`);
          }
        }
      };

      lRequestUpgrade.onsuccess = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Mantém a defesa contra deadlocks ativada na nova conexão
        db.onversionchange = () => {
          db.close();
          console.warn(`[IndexedDB] Conexão fechada para permitir atualização em outra aba.`);
        };
        resolve(db);
      };

      lRequestUpgrade.onblocked = () => {
        console.warn(`[IndexedDB] O banco está bloqueado. Feche as outras abas para o sistema criar as novas tabelas.`);
      };

      lRequestUpgrade.onerror = (event) => {
        console.error('Erro ao atualizar IndexedDB:', (event.target as IDBOpenDBRequest).error);
        reject((event.target as IDBOpenDBRequest).error);
      };
    });
  }

  protected async armazenar<R = any>(pChave: T | string, pDados: R): Promise<void> {
    return new Promise((resolve, reject) => {
      const transaction = this.database.transaction(this.storeName, 'readwrite');
      const store = transaction.objectStore(this.storeName);

      // IndexedDB aceita objetos, não precisa de stringify (mais performático)
      // O structuredClone é opcional, mas garante que o objeto seja "limpo"
      store.put({
        key: String(pChave),
        versao: version,
        timestamp: new Date().getTime(),
        data: new Date().toLocaleString(),
        dados: pDados, // Salva o objeto direto
      });

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  protected async recuperar<R = any>(pChave: T | string): Promise<R | null> {
    return new Promise((resolve, reject) => {
      const transaction = this.database.transaction(this.storeName, 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.get(String(pChave));

      request.onsuccess = () => {
        resolve(request.result?.dados ?? null);
      };
      request.onerror = () => reject(request.error);
    });
  }

  protected async excluir(pChave: T | string): Promise<void> {
    return new Promise((resolve, reject) => {
      const transaction = this.database.transaction(this.storeName, 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(String(pChave));

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
}
